import { chain, set, unset } from 'icepick';
import isEmpty from 'lodash/isEmpty';
import {
	StatusCategoryIds,
	StatusCategoryTypes,
} from '@atlassian/jira-common-constants/src/status-categories.tsx';
import { ChangeEventTypes } from '@atlassian/jira-issue-view-model/src/change-type.tsx';
import type { CardId } from '../../../../model/types.tsx';
import { SET_APP_DATA, type SetAppDataAction } from '../../../actions/app/index.tsx';
import {
	CARD_ASSIGNEE_SET,
	CARD_ASSIGNEE_SET_OPTIMISTIC,
	type CardAssigneeSetAction,
	type CardAssigneeSetOptimisticAction,
} from '../../../actions/assignee/index.tsx';
import {
	ASSIGN_TO_ME_REQUEST,
	type AssignToMeRequestAction,
} from '../../../actions/cards/assign-to-me/index.tsx';
import {
	CARDS_BULK_OPERATIONS_REQUESTS,
	type CardsBulkOperationsPayload,
	type CardsBulkOperationsRequestsAction,
} from '../../../actions/cards/bulk-operations/index.tsx';

import {
	CARDS_BULK_OPERATIONS_DELETE_REQUESTS,
	type CardsBulkOperationsDeleteRequestsAction,
} from '../../../actions/cards/bulk-operations-delete/index.tsx';
import {
	CARD_CREATE_END_OF_BACKLOG_OPTIMISTIC,
	CARD_CREATE_MULTIPLE_REQUEST,
	CARD_CREATE_SUCCESS,
	CARD_CREATE_UNMAPPED_WITH_ISSUE_LINK,
	type CardCreateEndOfBacklogOptimisticAction,
	type CardCreateMultipleRequestAction,
	type CardCreateSuccessAction,
	type CardCreateUnmappedWithIssueLinkAction,
} from '../../../actions/cards/create/index.tsx';
import {
	CARD_DELETE,
	CARD_DELETE_REQUEST,
	type CardDeleteAction,
	type CardDeleteRequestAction,
} from '../../../actions/cards/delete/index.tsx';
import {
	CARDS_FLAG_WITH_COMMENT_REQUEST,
	type CardsFlagWithCommentRequestAction,
} from '../../../actions/cards/flag-with-comment/index.tsx';
import {
	CARDS_MOVE_REQUEST,
	CARDS_MOVE_SUCCESS,
	type CardsMoveRequestAction,
	type CardsMoveSuccessAction,
} from '../../../actions/cards/move/index.tsx';
import {
	CARDS_PARENT_SET_OPTIMISTIC,
	type CardsParentSetOptimisticAction,
} from '../../../actions/cards/parent/index.tsx';
import {
	CARD_REFRESH_REQUEST,
	CARD_REFRESH_SUCCESS,
	type CardRefreshRequestAction,
	type CardRefreshSuccessAction,
} from '../../../actions/cards/refresh/index.tsx';
import {
	CARD_ESTIMATE_SET,
	CARD_ESTIMATE_SET_OPTIMISTIC,
	type CardEstimateSetAction,
	type CardEstimateSetOptimisticAction,
} from '../../../actions/estimate/index.tsx';
import {
	CARD_PARENT_ID_SET,
	CARD_PARENT_SET_OPTIMISTIC,
	type CardParentIdSetAction,
	type CardParentSetOptimisticAction,
} from '../../../actions/parent/index.tsx';
import {
	CARD_PRIORITY_SET,
	CARD_PRIORITY_SET_OPTIMISTIC,
	type CardPrioritySetAction,
	type CardPrioritySetOptimisticAction,
} from '../../../actions/priorities/index.tsx';
import { CARD_STATUS_SET, type CardStatusSetAction } from '../../../actions/status/index.tsx';
import { CARD_SUMMARY_SET, type CardSummarySetAction } from '../../../actions/summary/index.tsx';
import type { CardEntityState } from './types.tsx';
import {
	updateCardWithChangeEvent,
	buildCardFromEvent,
	transformBulkOperationsData,
} from './utils.tsx';

type Action =
	| AssignToMeRequestAction
	| SetAppDataAction
	| CardsBulkOperationsRequestsAction
	| CardsBulkOperationsDeleteRequestsAction
	| CardsFlagWithCommentRequestAction
	| CardCreateSuccessAction
	| CardsMoveRequestAction
	| CardsMoveSuccessAction
	| CardDeleteRequestAction
	| CardDeleteAction
	| CardRefreshRequestAction
	| CardRefreshSuccessAction
	| CardsParentSetOptimisticAction
	| CardCreateEndOfBacklogOptimisticAction
	| CardStatusSetAction
	| CardEstimateSetAction
	| CardEstimateSetOptimisticAction
	| CardAssigneeSetAction
	| CardAssigneeSetOptimisticAction
	| CardSummarySetAction
	| CardParentIdSetAction
	| CardParentSetOptimisticAction
	| CardCreateUnmappedWithIssueLinkAction
	| CardCreateMultipleRequestAction
	| CardPrioritySetAction
	| CardPrioritySetOptimisticAction;

const defaultState: CardEntityState = {};

const updateCardFlags = (
	state: CardEntityState,
	{ payload: { cards, flag } }: CardsFlagWithCommentRequestAction,
) => {
	const chainedState = chain(state);
	cards.forEach((id: CardId) => chainedState.assocIn([String(id), 'isFlagged'], flag));
	return chainedState.value();
};

const updateCardsParent = (
	state: CardEntityState,
	{ payload: { cards, parentId } }: CardsParentSetOptimisticAction,
) => {
	const chainedState = chain(state);
	cards.forEach((card: { id: number }) =>
		chainedState.assocIn([String(card.id), 'parentId'], parentId),
	);
	return chainedState.value();
};

const updateCardsDetails = (state: CardEntityState, payload: CardsBulkOperationsPayload[]) => {
	const chainedState = chain(state);
	payload.forEach(({ cardIds, data }) => {
		const dataTransformer = transformBulkOperationsData(data);
		cardIds.forEach((cardId) => {
			const id = String(cardId);
			const newData = dataTransformer(state[id]);

			const newCardData = chain(state[id]).assign(newData);
			chainedState.assoc(id, newCardData.value());
		});
	});
	return chainedState.value();
};

const setCardStatus = (
	state: CardEntityState,
	{ payload: { cardId, statusId, statusCategory, isCMP } }: CardStatusSetAction,
) => {
	const chainedState = chain(state);
	chainedState.assocIn([String(cardId), 'statusId'], statusId);

	// We use `isDone` to show issue key strikethrough as an indicator of issue resolution.
	// Only for TMP, status' category equaling `Done` means the issue is resolved.
	// For CMP, we cannot determine an issue's resolution completely client-side due to the complexities of non-simplified workflows.
	// As we're fire a data refresh request on each issue transition for CMP, the correct `isDone` value will be pulled from the server anyway with a slight delay.
	if (!isCMP) {
		const isDone =
			StatusCategoryIds[statusCategory] === StatusCategoryIds[StatusCategoryTypes.DONE];
		chainedState.assocIn([String(cardId), 'isDone'], isDone);
	}

	return chainedState.value();
};

const setCardEstimate = (
	state: CardEntityState,
	{ payload: { cardId, estimate } }: CardEstimateSetAction | CardEstimateSetOptimisticAction,
) => {
	const chainedState = chain(state);

	chainedState.merge({ [String(cardId)]: { estimate } });

	return chainedState.value();
};

const setCardAssignee = (
	state: CardEntityState,
	{
		payload: { cardId, assignee },
	}: CardAssigneeSetAction | AssignToMeRequestAction | CardAssigneeSetOptimisticAction,
) => {
	const chainedState = chain(state);
	chainedState.assocIn([String(cardId), 'assignee'], assignee);
	return chainedState.value();
};

const setCardSummary = (
	state: CardEntityState,
	{ payload: { cardId, summary } }: CardSummarySetAction,
) => {
	const chainedState = chain(state);
	chainedState.assocIn([String(cardId), 'summary'], summary);
	return chainedState.value();
};

const setCardParentId = (
	state: CardEntityState,
	{ payload: { cardId, parentId } }: CardParentIdSetAction,
) => {
	const chainedState = chain(state);
	chainedState.assocIn([String(cardId), 'parentId'], parentId);
	return chainedState.value();
};

const setCardParent = (
	state: CardEntityState,
	{ payload: { cardId, parent } }: CardParentSetOptimisticAction,
) => {
	const chainedState = chain(state);
	chainedState
		.assocIn([String(cardId), 'parentId'], (parent?.id && Number(parent.id)) ?? null)
		.assocIn([String(cardId), 'parentKey'], parent?.key ?? null);
	return chainedState.value();
};

const setCardTargetStatusId = (
	state: CardEntityState,
	{ payload: { cardIds, targetStatusId } }: CardsMoveRequestAction | CardsMoveSuccessAction,
) => {
	if (isEmpty(cardIds) || targetStatusId === undefined) {
		return state;
	}

	const chainedState = chain(state);
	cardIds.forEach((cardId) => {
		chainedState.assocIn([String(cardId), 'statusId'], targetStatusId);
	});
	return chainedState.value();
};

const setCardPriority = (
	state: CardEntityState,
	{ payload: { cardId, priority } }: CardPrioritySetOptimisticAction | CardPrioritySetAction,
) => {
	const { id, ...cardPriority } = priority;
	const chainedState = chain(state);
	chainedState.assocIn([String(cardId), 'priority'], cardPriority);
	return chainedState.value();
};

const cardReducer = (state: CardEntityState = defaultState, action: Action): CardEntityState => {
	switch (action.type) {
		case SET_APP_DATA:
			return action.payload.entities.card;
		case CARD_CREATE_MULTIPLE_REQUEST: {
			const { tempIds, summaries, typeId, typeIcon } = action.payload;
			const chainState = chain(state);
			const defaultCardProps = {
				key: '',
				labels: [],
				assignee: null,
				isDone: false,
				isFlagged: false,
				issueUrl: '',
				statusId: '',
				parentId: null,
				priority: null,
				dueDate: null,
				estimate: null,
				numCompleteChildren: 0,
				numTotalChildren: 0,
				fixVersions: [],
			};
			for (let i = 0; i < tempIds.length; i += 1) {
				const tempId = tempIds[i];
				const summary = summaries[i];
				chainState.set(String(tempId), {
					...defaultCardProps,
					summary,
					id: tempId,
					icon: typeIcon,
					typeId,
				});
			}
			return chainState.value();
		}
		case CARD_CREATE_SUCCESS: {
			const { card, temporaryCardId } = action.payload;

			return chain(state).unset(String(temporaryCardId)).set(String(card.id), card).value();
		}
		case CARDS_MOVE_REQUEST:
		case CARDS_MOVE_SUCCESS:
			return setCardTargetStatusId(state, action);
		case CARDS_FLAG_WITH_COMMENT_REQUEST:
			return updateCardFlags(state, action);
		case CARD_DELETE_REQUEST:
		case CARD_DELETE:
			return unset(state, String(action.payload.cardId));
		case CARD_CREATE_UNMAPPED_WITH_ISSUE_LINK: {
			const stateChain = chain(state);
			action.payload.cardsIdentifiers.forEach(({ id }) => {
				stateChain.unset(String(id));
			});
			return stateChain.value();
		}
		case CARD_REFRESH_REQUEST: {
			const { cardId, changeEvent } = action.payload;
			if (cardId == null || !changeEvent) {
				return state;
			}

			const cardIdStr = String(cardId);
			const oldCard = state[cardIdStr];
			if (!oldCard) {
				// This should only happen when the updated card is a subtask which doesn't exist in the state
				return state;
			}
			const optimisticCard = updateCardWithChangeEvent(oldCard, changeEvent);
			return set(state, cardIdStr, optimisticCard);
		}
		case CARD_REFRESH_SUCCESS:
			return set(state, String(action.payload.id), action.payload);
		case CARDS_PARENT_SET_OPTIMISTIC:
			return updateCardsParent(state, action);
		case CARD_CREATE_END_OF_BACKLOG_OPTIMISTIC: {
			const { changeEvent } = action.payload;
			if (changeEvent.type === ChangeEventTypes.CHILD_ISSUE_ADDED) {
				const [cardId, newCard] = buildCardFromEvent(changeEvent);
				return set(state, cardId, newCard);
			}
			return state;
		}
		case CARD_STATUS_SET: {
			return setCardStatus(state, action);
		}

		case CARD_ESTIMATE_SET_OPTIMISTIC:
		case CARD_ESTIMATE_SET: {
			return setCardEstimate(state, action);
		}
		case CARD_ASSIGNEE_SET_OPTIMISTIC:
		case ASSIGN_TO_ME_REQUEST:
		case CARD_ASSIGNEE_SET: {
			return setCardAssignee(state, action);
		}
		case CARD_SUMMARY_SET: {
			return setCardSummary(state, action);
		}
		case CARD_PARENT_ID_SET: {
			return setCardParentId(state, action);
		}
		case CARD_PARENT_SET_OPTIMISTIC:
			return setCardParent(state, action);

		case CARD_PRIORITY_SET_OPTIMISTIC:
		case CARD_PRIORITY_SET: {
			return setCardPriority(state, action);
		}

		case CARDS_BULK_OPERATIONS_REQUESTS: {
			return updateCardsDetails(state, action.payload);
		}

		case CARDS_BULK_OPERATIONS_DELETE_REQUESTS: {
			const stateChain = chain(state);
			action.payload.forEach(({ cardIds }) => {
				Object.entries(state).forEach(([cardId, value]) => {
					if (
						cardIds.includes(Number(cardId)) ||
						(value.parentId !== null && cardIds.includes(value.parentId))
					) {
						stateChain.unset(String(cardId));
					}
				});
			});
			return stateChain.value();
		}

		default:
			return state;
	}
};

export default cardReducer;
